<template>
  <div class="pa-deliverables">
    <div v-if="loading" class="pa-deliverables__loading mainColor--text">Loading your deliverables…</div>

    <div v-else-if="error" class="pa-trackcard">
      <i class="material-icons" aria-hidden="true">warning</i>
      <p><b>We could not load your deliverables.</b> {{ error }}</p>
      <button type="button" class="pa-btn pa-btn--orange-out" @click="load()">Retry</button>
    </div>

    <template v-else>
      <!-- publishing is paused because our team cannot log in -->
      <div v-if="blockedOrder" class="pa-trackcard">
        <i class="material-icons" aria-hidden="true">vpn_key</i>
        <p>
          <b>Publishing is paused: we cannot access your site.</b>
          {{ blockedOrder.publishingBlockedNote
            || 'Re-add our editor account, then tell us to retry.' }}
        </p>
        <button type="button" class="pa-btn pa-btn--orange" @click="siteAccessOpen = true">
          Update site access
        </button>
      </div>

      <div class="pa-chips" role="tablist" aria-label="Deliverable type">
        <button
          type="button"
          role="tab"
          class="pa-chip"
          :class="{ 'pa-chip--sel': subTab === 'content' }"
          :aria-selected="subTab === 'content'"
          @click="subTab = 'content'"
        >
          Content<span class="pa-chip__n">{{ rows.length }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="pa-chip"
          :class="{ 'pa-chip--sel': subTab === 'boosters' }"
          :aria-selected="subTab === 'boosters'"
          @click="subTab = 'boosters'"
        >
          Boosters<span class="pa-chip__n">{{ boosterRows.length }}</span>
        </button>
      </div>

      <template v-if="subTab === 'content'">
        <div v-if="!rows.length" class="pa-formcard">
          <h3>No deliverables yet</h3>
          <p class="pa-formcard__sub">{{ emptyHint }}</p>
        </div>

        <template v-else>
          <div class="pa-chips" role="tablist" aria-label="Filter by state">
            <button
              v-for="chip in POP_AUTHORITY_DELIVERABLE_FILTERS"
              :key="chip.id"
              type="button"
              role="tab"
              class="pa-chip"
              :class="{ 'pa-chip--sel': filter === chip.id }"
              :aria-selected="filter === chip.id"
              @click="filter = chip.id"
            >
              {{ chip.label }}<span class="pa-chip__n">{{ filterCounts[chip.id] || 0 }}</span>
            </button>
          </div>

          <!-- one flag per project: the site has one editor account, so every order with the
               publishing team is paused or resumed together -->
          <div v-if="isAdmin && publishingOrders.length" class="pa-deliverables__admin">
            <div class="pa-deliverables__admin-head">
              <span class="pa-deliverables__admin-label">Admin</span>
              <b>Publishing access</b>
            </div>
            <div class="pa-deliverables__admin-row">
              <label class="pa-check" :class="{ 'pa-check--sel': adminBlocked }">
                <span class="pa-check__box">
                  <i v-if="adminBlocked" class="material-icons" aria-hidden="true">check</i>
                </span>
                <input
                  v-model="adminBlocked"
                  type="checkbox"
                  class="pa-deliverables__admin-check"
                />
                <span>We cannot access the site: publishing is paused for {{ publishingOrdersLabel }}</span>
              </label>
              <input
                v-model="adminBlockedNote"
                class="pa-in"
                maxlength="4000"
                placeholder="What do we need to publish? (editor invite, new login, plugin access...)"
              />
              <button
                type="button"
                class="pa-btn pa-btn--out pa-btn--admin"
                :disabled="busy"
                @click="saveBlocked"
              >
                Save publishing access
              </button>
            </div>
          </div>

          <div v-if="!filteredRows.length" class="pa-deliverables__empty-filter">
            No articles match this filter.
          </div>

          <div v-else class="pa-tablewrap">
            <table class="pa-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Source</th>
                  <th>Keyword</th>
                  <th>Content type</th>
                  <th>Order date</th>
                  <th>Delivery period</th>
                  <th>Delivery due date</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredRows" :key="rowKey(row)">
                  <td>
                    <!-- one weight (700, as the design's <b>): a <b> inside the 700 link rendered "bolder" = 900 -->
                    <router-link v-if="row.siloId" :to="planPath(row)" class="pa-deliverables__order">#{{ row.orderId }}</router-link>
                    <b v-else>#{{ row.orderId }}</b>
                  </td>
                  <td>{{ sourceLabel(row) }}</td>
                  <td>
                    {{ row.keyword || '—' }}
                    <div v-if="row.liveUrl" class="pa-deliverables__live">
                      <a :href="row.liveUrl" target="_blank" rel="noopener noreferrer">
                        {{ shortUrl(row.liveUrl) }}
                      </a>
                    </div>
                  </td>
                  <td>{{ row.contentTypeLabel || '—' }}</td>
                  <td>{{ dateLabel(row.orderDate) }}</td>
                  <td>{{ row.deliveryPeriodLabel || '—' }}</td>
                  <td>{{ dateLabel(row.dueDate) }}</td>
                  <td class="pa-deliverables__price">Included</td>
                  <td>
                    <span class="pa-status" :class="`pa-status--${chip(row).tone}`">
                      {{ chip(row).label }}</span>
                    <!-- one status per article (design); the order's own status is set in Order history.
                         Approved / Published dates on their own lines (design ContentTab) -->
                    <div v-if="statusSubLines(row).length" class="pa-deliverables__sub">
                      <div v-for="line in statusSubLines(row)" :key="line">{{ line }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="pa-deliverables__acts">
                      <a
                        v-if="row.liveUrl"
                        class="pa-act"
                        :href="row.liveUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tip="View live page"
                        aria-label="View live page"
                      ><i class="material-icons" aria-hidden="true">open_in_new</i></a>

                      <button
                        v-if="canViewContent(row)"
                        type="button"
                        class="pa-act"
                        data-tip="View content"
                        aria-label="View content"
                        @click="viewContent(row)"
                      ><i class="material-icons" aria-hidden="true">description</i></button>

                      <span
                        v-if="canDownload(row)"
                        class="pa-deliverables__download"
                        :data-tip="isAdmin ? 'More actions' : 'Download'"
                      >
                        <silo-action-menu
                          :key="'menu-' + row.siloKeywordId"
                          :siloKeyword="row.siloKeyword"
                          :silo="row.silo"
                          :isWhiteGloveUser="isWhiteGloveFamilyUser"
                          :isMissionControlSession="isAdmin"
                          :isOrderedReport="true"
                          :orderedReportType="row.orderedReportType || 2"
                          :prefer-edited-drafts="true"
                          @refreshData="() => {}"
                        />
                      </span>

                      <button
                        v-if="canApprove(row)"
                        type="button"
                        class="pa-act pa-act--approve"
                        data-tip="Approve"
                        aria-label="Approve"
                        :disabled="busy"
                        @click="approve(row)"
                      ><i class="material-icons-outlined" aria-hidden="true">thumb_up</i></button>

                      <button
                        v-if="canRequestUpdates(row)"
                        type="button"
                        class="pa-act"
                        data-tip="Request updates"
                        aria-label="Request updates"
                        :disabled="busy"
                        @click="updatesRow = row"
                      ><i class="material-icons" aria-hidden="true">edit_note</i></button>

                      <button
                        v-if="canMarkRevised(row)"
                        type="button"
                        class="pa-btn pa-btn--out pa-btn--admin pa-deliverables__small"
                        :disabled="busy"
                        @click="markRevised(row)"
                      >Mark revised (admin)</button>

                      <button
                        v-if="canAdminPublish(row)"
                        type="button"
                        class="pa-btn pa-btn--out pa-btn--admin pa-deliverables__small"
                        :disabled="busy"
                        @click="openLiveUrl(row, true)"
                      >
                        {{ row.liveUrl ? 'Edit live page (admin)' : 'Add live page (admin)' }}
                      </button>

                      <button
                        v-else-if="canAddLiveUrl(row)"
                        type="button"
                        class="pa-btn pa-btn--orange-out pa-deliverables__small"
                        :disabled="busy"
                        @click="openLiveUrl(row, false)"
                      >Add live URL</button>

                      <span v-if="!hasAnyAction(row)" class="pa-deliverables__pending">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="countdown.pending" class="pa-deliverables__bar">
            <div class="pa-deliverables__bar-text">
              <i class="material-icons" aria-hidden="true">schedule</i>
              <span>
                <b>{{ countdown.pending }} of {{ countdown.total }}</b> still need your approval.
                <template v-if="countdown.days === 0">They auto-approve today.</template>
                <template v-else-if="countdown.days">
                  <b>{{ countdown.days }} business {{ countdown.days === 1 ? 'day' : 'days' }} left</b>
                  before they auto-approve ({{ countdown.weekdayLabel }}, {{ countdown.dateLabel }}).
                </template>
              </span>
            </div>
            <button
              type="button"
              class="pa-btn pa-btn--orange pa-btn--pill"
              :disabled="busy"
              @click="approveAllOpen = true"
            >Approve everything</button>
          </div>

          <p v-else-if="selfPublish" class="pa-deliverables__note">
            You chose to publish content yourself: delivered pieces are yours to publish whenever you
            are ready, no approval needed. Instructions are included with each document. Once a page is
            live, add its URL so we can track it.
          </p>
        </template>
      </template>

      <!-- Boosters (Phase 8): delivery rows of the booster pack orders -->
      <template v-else>
        <div v-if="boostersError && !boostersState" class="pa-trackcard">
          <i class="material-icons" aria-hidden="true">warning</i>
          <p><b>We could not load your booster orders.</b> {{ boostersError }}</p>
          <button type="button" class="pa-btn pa-btn--orange-out" @click="loadBoosters()">Retry</button>
        </div>

        <div v-else-if="!boosterRows.length" class="pa-formcard">
          <h3>No booster deliveries yet</h3>
          <p class="pa-formcard__sub">
            Booster pack orders and their delivery reports will appear here.
            <router-link class="pa-link" :to="boostersRoute">Browse booster packs</router-link>
          </p>
        </div>

        <template v-else>
          <div class="pa-tablewrap">
            <table class="pa-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Source</th>
                  <th>Details</th>
                  <th>Content type</th>
                  <th>Order date</th>
                  <th>Delivery period</th>
                  <th>Delivery due date</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in boosterRows" :key="'booster-' + row.id">
                  <td><b>#{{ row.id }}</b></td>
                  <td>Booster pack</td>
                  <td>
                    {{ boosterDetails(row) }}
                    <div v-if="boosterProgress(row)" class="pa-deliverables__sub">{{ boosterProgress(row) }}</div>
                  </td>
                  <td>Booster</td>
                  <td>{{ dateLabel(boosterOrderDate(row)) }}</td>
                  <td>{{ boosterPeriod(row) }}</td>
                  <td>{{ boosterDue(row) }}</td>
                  <td class="pa-deliverables__price">${{ money(boosterPrice(row)) }}</td>
                  <td>
                    <span class="pa-status" :class="`pa-status--${boosterChip(row).tone}`">
                      {{ boosterChip(row).label }}</span>
                  </td>
                  <td>
                    <div class="pa-deliverables__acts">
                      <a
                        v-if="boosterReportUrl(row)"
                        class="pa-act"
                        :href="boosterReportUrl(row)"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tip="View report"
                        aria-label="View report"
                      ><i class="material-icons" aria-hidden="true">summarize</i></a>

                      <template v-if="isAdmin">
                        <button
                          v-if="boosterKey(row) === POP_AUTHORITY_BOOSTER_STATUS.ORDERED"
                          type="button"
                          class="pa-btn pa-btn--out pa-btn--admin pa-deliverables__small"
                          :disabled="busy || boostersBusy"
                          @click="boosterStartRow = row"
                        >Mark in delivery (admin)</button>

                        <button
                          v-if="boosterCanDeliver(row)"
                          type="button"
                          class="pa-btn pa-btn--out pa-btn--admin pa-deliverables__small"
                          :disabled="busy || boostersBusy"
                          @click="boosterReportRow = row"
                        >
                          <i class="material-icons" aria-hidden="true">add_link</i>
                          Add report link (admin)
                        </button>
                        <button
                          v-else-if="boosterKey(row) === POP_AUTHORITY_BOOSTER_STATUS.DELIVERED"
                          type="button"
                          class="pa-btn pa-btn--out pa-btn--admin pa-deliverables__small"
                          :disabled="busy || boostersBusy"
                          @click="boosterReportRow = row"
                        >Edit link (admin)</button>
                      </template>

                      <span v-if="!boosterHasAction(row)" class="pa-deliverables__pending">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="pa-deliverables__note">
            Booster deliveries don't need your approval. Each is due 7 days after order; when your SEO Manager
            links the report the status turns Delivered and the report opens from here and from your Google
            folder.
          </p>
        </template>
      </template>
    </template>

    <booster-report-modal
      v-if="boosterReportRow"
      :details="boosterDetails(boosterReportRow)"
      :initial-url="boosterReportUrl(boosterReportRow)"
      :mode="boosterKey(boosterReportRow) === POP_AUTHORITY_BOOSTER_STATUS.DELIVERED ? 'edit' : 'deliver'"
      :busy="boostersBusy"
      @close="boosterReportRow = null"
      @confirm="saveBoosterReport"
    />

    <booster-confirm-modal
      v-if="boosterStartRow"
      icon="local_shipping"
      title="Mark in delivery (admin)"
      :body="`Tells the client work has started on “${boosterDetails(boosterStartRow)}”. `
        + 'Add a short progress note if you want it shown next to the status.'"
      reason-label="Progress note (optional)"
      reason-placeholder="e.g. 2 of 4 links placed"
      confirm-label="Mark in delivery"
      :busy="boostersBusy"
      @close="boosterStartRow = null"
      @confirm="startBoosterDelivery"
    />

    <request-updates-modal
      v-if="updatesRow"
      :keyword="updatesRow.keyword"
      :busy="busy"
      @close="updatesRow = null"
      @confirm="requestUpdates"
    />

    <live-page-modal
      v-if="liveUrlRow"
      :keyword="liveUrlRow.keyword"
      :initial-url="liveUrlRow.liveUrl || ''"
      :is-admin="liveUrlAsAdmin"
      :busy="busy"
      @close="liveUrlRow = null"
      @confirm="saveLiveUrl"
    />

    <site-access-modal
      v-if="siteAccessOpen"
      :site-name="(site && site.name) || ''"
      :blocked-note="(blockedOrder && blockedOrder.publishingBlockedNote) || ''"
      :busy="busy"
      @close="closeSiteAccess"
      @confirm="retryAccess"
    />

    <!-- design pa-app.jsx Modal: round icon, title, muted body, Cancel + orange confirm -->
    <div
      v-if="approveAllOpen"
      class="pa-ovl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pa-approve-all-title"
      @click.self="approveAllOpen = false"
    >
      <div class="pa-cmodal">
        <div class="pa-cmodal__icon"><i class="material-icons" aria-hidden="true">thumb_up</i></div>
        <h3 id="pa-approve-all-title" class="pa-cmodal__title">Approve everything?</h3>
        <p class="pa-cmodal__body">
          This approves the {{ countdown.pending }}
          {{ countdown.pending === 1 ? 'article' : 'articles' }} still waiting for your review.
          Articles you asked us to update are left alone. Approved content goes to our publishing
          team and can no longer be revised.
        </p>
        <div class="pa-cmodal__acts">
          <button type="button" class="pa-btn pa-btn--out" :disabled="busy" @click="approveAllOpen = false">Cancel</button>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="busy" @click="approveAll">
            Yes, approve everything
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SiloActionMenu from '@/components/Campaigns/silo/SiloActionMenu.vue'
import RequestUpdatesModal from '@/components/SitesSingleView/PopAuthority/RequestUpdatesModal.vue'
import LivePageModal from '@/components/SitesSingleView/PopAuthority/LivePageModal.vue'
import SiteAccessModal from '@/components/SitesSingleView/PopAuthority/SiteAccessModal.vue'
import BoosterConfirmModal from '@/components/SitesSingleView/PopAuthority/BoosterConfirmModal.vue'
import BoosterReportModal from '@/components/SitesSingleView/PopAuthority/BoosterReportModal.vue'
import boostersMixin from '@/components/SitesSingleView/PopAuthority/boostersMixin'
import {
  POP_AUTHORITY_BOOSTER_STATUS,
  POP_AUTHORITY_BOOSTERS_ROUTE,
  POP_AUTHORITY_DELIVERABLE_FILTERS,
  POP_AUTHORITY_PUBLISHING_MODE,
  popAuthorityBoosterChip,
  popAuthorityBoosterDeliveryPeriodLabel,
  popAuthorityBoosterDetails,
  popAuthorityBoosterDueLabel,
  popAuthorityBoosterIsCanceled,
  popAuthorityBoosterOrderDate,
  popAuthorityBoosterPrice,
  popAuthorityBoosterProgressNote,
  popAuthorityBoosterReportUrl,
  popAuthorityBoosterStatusKey,
  popAuthorityDeliverableCanAddLiveUrl,
  popAuthorityDeliverableCanAdminMarkRevised,
  popAuthorityDeliverableCanAdminPublish,
  popAuthorityDeliverableCanApprove,
  popAuthorityDeliverableCanRequestUpdates,
  popAuthorityDeliverableChip,
  popAuthorityDeliverableFilterCounts,
  popAuthorityDeliverableIsDelivered,
  popAuthorityDeliverableRowState,
  popAuthorityParseUtc,
  popAuthorityReviewCountdown,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

/**
 * POP Authority Deliverables tab (design pa-tabs.jsx ContentTab + pa-app.jsx modals): one row per
 * ordered article with its review window, approval, revision and live URL, plus the Boosters
 * sub-tab (Phase 8) with the delivery state of the booster pack orders.
 *
 * The backend decides every transition (helpers/pop_authority_deliverables.py,
 * helpers/pop_authority_boosters.py) and returns the whole screen state after each action, so this
 * component never patches rows locally.
 */
export default {
  name: 'DeliverablesPopAuthority',
  components: {
    BoosterConfirmModal, BoosterReportModal, LivePageModal, RequestUpdatesModal, SiloActionMenu, SiteAccessModal,
  },
  mixins: [boostersMixin],
  inject: {
    refreshWhiteGloveOrders: { default: null },
    getPopAuthoritySiteStatus: { default: null },
  },
  data () {
    return {
      POP_AUTHORITY_DELIVERABLE_FILTERS,
      POP_AUTHORITY_BOOSTER_STATUS,
      loading: true,
      busy: false,
      error: '',
      state: null,
      subTab: 'content',
      filter: 'all',
      updatesRow: null,
      liveUrlRow: null,
      liveUrlAsAdmin: false,
      siteAccessOpen: false,
      approveAllOpen: false,
      adminBlocked: false,
      adminBlockedNote: '',
      boosterReportRow: null,
      boosterStartRow: null,
    }
  },
  computed: {
    siteId () {
      return Number(this.$route.params.siteId)
    },
    site () {
      return this.$store.getters.siteById(this.siteId) || null
    },
    siteStatus () {
      return typeof this.getPopAuthoritySiteStatus === 'function'
        ? this.getPopAuthoritySiteStatus()
        : null
    },
    rows () {
      return (this.state && this.state.rows) || []
    },
    orders () {
      return (this.state && this.state.orders) || []
    },
    isMissionControlSession () {
      return Boolean(this.state && this.state.isAdmin)
    },
    /** Admin tooling follows the Admin toggle, like the other POP Authority tabs. */
    isAdmin () {
      return this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    isWhiteGloveFamilyUser () {
      // AllContent.vue's own check excludes POP Authority plans, so the download menu needs this one
      return this.$store.getters.isWhiteGloveFamilyUser
    },
    publishingMode () {
      const fromApi = this.state && this.state.publishingMode
      const fromSite = this.site && this.site.publishing_mode
      return fromApi || fromSite || POP_AUTHORITY_PUBLISHING_MODE.WE_PUBLISH
    },
    selfPublish () {
      return this.publishingMode === POP_AUTHORITY_PUBLISHING_MODE.SELF_PUBLISH
    },
    filterCounts () {
      return popAuthorityDeliverableFilterCounts(this.rows, this.publishingMode)
    },
    filteredRows () {
      if (this.filter === 'all') return this.rows
      return this.rows.filter(
        (row) => popAuthorityDeliverableRowState(row, this.publishingMode) === this.filter
      )
    },
    countdown () {
      return popAuthorityReviewCountdown(this.rows, this.publishingMode)
    },
    blockedOrder () {
      return this.orders.find((order) => order.publishingBlockedAt) || null
    },
    /** Orders with our publishing team: the admin flags or clears site access for all of them at once. */
    publishingOrders () {
      if (this.selfPublish) return []
      return this.orders.filter((order) => order.publishingRequestedAt)
    },
    publishingOrdersLabel () {
      const n = this.publishingOrders.length
      return n === 1 ? 'the 1 order with our publishing team' : `the ${n} orders with our publishing team`
    },
    emptyHint () {
      const status = this.siteStatus
      const live = status && status.package && status.package.subscription_status === 'active'
      if (live) {
        return 'Pages produced under your package will appear here. '
          + 'First delivery expected about a week after your first payment.'
      }
      return 'Pages produced under your package will appear here, with links to review each piece. '
        + 'Production starts once you choose a package.'
    },
    /** Booster orders with a delivery (canceled ones live in Order history only). */
    boosterRows () {
      return this.boosterOrders.filter((row) => !popAuthorityBoosterIsCanceled(row))
    },
    boostersRoute () {
      return { name: POP_AUTHORITY_BOOSTERS_ROUTE, params: { siteId: String(this.siteId) } }
    },
  },
  watch: {
    '$route.params.siteId': function () {
      this.load()
    },
    '$route.query.action': function (value) {
      if (value === 'site-access') this.openSiteAccessFromQuery()
    },
    '$route.query.tab': function () {
      this.applyTabQuery()
    },
    '$store.state.triggerRockyRefresh': function () {
      this.load({ silent: true })
    },
  },
  mounted () {
    this.applyTabQuery()
    this.load().then(() => {
      if (this.$route.query.action === 'site-access') this.openSiteAccessFromQuery()
    })
  },
  methods: {
    load (opts = {}) {
      const silent = opts.silent === true
      if (!silent) {
        this.loading = true
        this.error = ''
      }
      // the Boosters sub-tab has its own fetch; a failure there never blocks the content table
      this.loadBoosters({ silent: true })
      return this.$store
        .dispatch('getPopAuthorityDeliverables', { siteId: this.siteId })
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status === 'SUCCESS') {
            this.apply(data)
          } else if (!silent) {
            this.error = data.msg || 'Please try again in a moment.'
          }
        })
        .catch((e) => {
          if (!silent) {
            this.error = (e && e.message) || 'Please try again in a moment.'
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    apply (data) {
      this.state = data
      const blocked = (data.orders || []).find((order) => order.publishingBlockedAt) || null
      this.adminBlocked = Boolean(blocked)
      this.adminBlockedNote = (blocked && blocked.publishingBlockedNote) || ''
    },
    /** Every mutation returns the whole state; the banner and the tab pulse follow. */
    runAction (action, payload, successText) {
      if (this.busy) return Promise.resolve(false)
      this.busy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, payload)
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status === 'SUCCESS') {
            this.apply(data)
            // design: confirmations use the teal toast (PaToast in the layout); errors stay red notifications
            if (successText) showPopAuthorityToast(this, successText)
            if (typeof this.refreshWhiteGloveOrders === 'function') {
              this.refreshWhiteGloveOrders()
            }
            return true
          }
          this.$notify({
            group: 'info',
            type: 'error',
            text: data.msg || 'That did not work, please try again.',
          })
          if (data.rows) this.apply(data)
          return false
        })
        .catch((e) => {
          const msg = (e && e.response && e.response.data && e.response.data.msg)
            || 'That did not work, please try again.'
          this.$notify({ group: 'info', type: 'error', text: msg })
          return false
        })
        .finally(() => {
          this.busy = false
          this.$store.commit('hideLoading')
        })
    },
    approve (row) {
      return this.runAction(
        'approvePopAuthorityDeliverable',
        { deliverableId: row.id },
        'Approved. It will be published with the rest of the order'
      )
    },
    approveAll () {
      const orderIds = this.countdown.orderIds
      this.approveAllOpen = false
      if (!orderIds.length) return Promise.resolve()
      return orderIds
        .reduce(
          (chain, orderId) => chain.then(() => this.runAction(
            'approveAllPopAuthorityOrderDeliverables', { orderId }
          )),
          Promise.resolve()
        )
        .then(() => {
          showPopAuthorityToast(this, 'Content approved and sent for publishing')
        })
    },
    requestUpdates ({ text }) {
      const row = this.updatesRow
      if (!row) return Promise.resolve()
      return this.runAction(
        'requestPopAuthorityDeliverableUpdates',
        { deliverableId: row.id, body: { text } },
        'Update request sent to the writing team'
      ).then((ok) => {
        if (ok) this.updatesRow = null
      })
    },
    markRevised (row) {
      return this.runAction(
        'markPopAuthorityDeliverableRevised',
        { deliverableId: row.id },
        'Marked revised: the review window restarted and the client was emailed'
      )
    },
    openLiveUrl (row, asAdmin) {
      this.liveUrlAsAdmin = Boolean(asAdmin)
      this.liveUrlRow = row
    },
    saveLiveUrl ({ liveUrl }) {
      const row = this.liveUrlRow
      if (!row) return Promise.resolve()
      const asAdmin = this.liveUrlAsAdmin
      return this.runAction(
        asAdmin ? 'publishPopAuthorityDeliverable' : 'setPopAuthorityDeliverableLiveUrl',
        { deliverableId: row.id, body: { liveUrl } },
        asAdmin
          ? 'Published and added to Wins tracking'
          : 'Thanks! That page is now tracked under Wins'
      ).then((ok) => {
        if (ok) this.liveUrlRow = null
      })
    },
    saveBlocked () {
      return this.runAction(
        'setPopAuthoritySitePublishingBlocked',
        {
          siteId: this.siteId,
          body: {
            blocked: Boolean(this.adminBlocked),
            note: this.adminBlockedNote || null,
          },
        },
        this.adminBlocked
          ? 'Publishing paused: the client was asked to restore access'
          : 'Publishing access cleared: the team can continue'
      )
    },
    retryAccess ({ note }) {
      if (!this.blockedOrder) {
        this.closeSiteAccess()
        return Promise.resolve()
      }
      return this.runAction(
        'retryPopAuthoritySitePublishingAccess',
        { siteId: this.siteId, body: { note: note || null } },
        'Thanks! Our publishing team will retry within 1 business day'
      ).then((ok) => {
        if (ok) this.closeSiteAccess()
      })
    },
    openSiteAccessFromQuery () {
      this.siteAccessOpen = true
      this.stripActionQuery()
    },
    closeSiteAccess () {
      this.siteAccessOpen = false
      this.stripActionQuery()
    },
    stripActionQuery () {
      this.stripQuery('action')
    },
    stripQuery (key) {
      if (!(key in (this.$route.query || {}))) return
      const query = { ...this.$route.query }
      delete query[key]
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => {})
    },
    /** `?tab=boosters` (Overview CTAs, emails) opens the Boosters sub-tab straight away. */
    applyTabQuery () {
      if (this.$route.query.tab !== 'boosters') return
      this.subTab = 'boosters'
      this.stripQuery('tab')
    },
    // --- Boosters sub-tab (Phase 8) ---
    boosterKey: popAuthorityBoosterStatusKey,
    boosterChip: popAuthorityBoosterChip,
    boosterDetails: popAuthorityBoosterDetails,
    boosterDue: popAuthorityBoosterDueLabel,
    boosterPeriod: popAuthorityBoosterDeliveryPeriodLabel,
    boosterOrderDate: popAuthorityBoosterOrderDate,
    boosterPrice: popAuthorityBoosterPrice,
    boosterReportUrl: popAuthorityBoosterReportUrl,
    boosterProgress: popAuthorityBoosterProgressNote,
    boosterCanDeliver (row) {
      return [
        POP_AUTHORITY_BOOSTER_STATUS.ORDERED,
        POP_AUTHORITY_BOOSTER_STATUS.IN_DELIVERY,
      ].includes(this.boosterKey(row))
    },
    boosterHasAction (row) {
      if (this.boosterReportUrl(row)) return true
      return this.isAdmin && this.boosterKey(row) !== POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT
    },
    money (v) {
      const n = Number(v) || 0
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    saveBoosterReport ({ reportUrl }) {
      const row = this.boosterReportRow
      if (!row) return Promise.resolve()
      const editing = this.boosterKey(row) === POP_AUTHORITY_BOOSTER_STATUS.DELIVERED
      return this.runBoosterAction(
        editing ? 'setPopAuthorityBoosterOrderReportUrl' : 'deliverPopAuthorityBoosterOrder',
        { orderId: row.id, body: { reportUrl } },
        editing
          ? 'Report link updated.'
          : 'Report linked and the booster is marked Delivered. The client was emailed.'
      ).then((data) => {
        if (data) this.boosterReportRow = null
      })
    },
    startBoosterDelivery ({ reason }) {
      const row = this.boosterStartRow
      if (!row) return Promise.resolve()
      return this.runBoosterAction(
        'startPopAuthorityBoosterOrderDelivery',
        { orderId: row.id, body: { progressNote: reason || null } },
        'Booster marked In delivery.'
      ).then((data) => {
        if (data) this.boosterStartRow = null
      })
    },
    chip (row) {
      return popAuthorityDeliverableChip(row)
    },
    canApprove (row) {
      return popAuthorityDeliverableCanApprove(row, this.publishingMode)
    },
    canRequestUpdates (row) {
      return popAuthorityDeliverableCanRequestUpdates(row, this.publishingMode)
    },
    canAddLiveUrl (row) {
      return popAuthorityDeliverableCanAddLiveUrl(row, this.publishingMode)
    },
    canAdminPublish (row) {
      return popAuthorityDeliverableCanAdminPublish(row, this.publishingMode, this.isAdmin)
    },
    canMarkRevised (row) {
      return popAuthorityDeliverableCanAdminMarkRevised(row, this.isAdmin)
    },
    /** The article body lives in the content editor, opened with the page run's secret key. */
    canViewContent (row) {
      if (!row.secretKey) return false
      return popAuthorityDeliverableIsDelivered(row) || this.isAdmin
    },
    canDownload (row) {
      if (!row.siloKeyword || !row.siloKeyword.pageRunId) return false
      return popAuthorityDeliverableIsDelivered(row) || this.isAdmin
    },
    hasAnyAction (row) {
      return Boolean(
        row.liveUrl
        || this.canViewContent(row)
        || this.canDownload(row)
        || this.canApprove(row)
        || this.canRequestUpdates(row)
        || this.canAddLiveUrl(row)
        || this.canAdminPublish(row)
        || this.canMarkRevised(row)
      )
    },
    viewContent (row) {
      // NOTE: mirrors WhiteGlove/AllContent.vue handleViewContent
      let url = `/content-editor?secretKey=${row.secretKey}&siloKeywordId=${row.siloKeywordId}`
      const relation = this.$store.state.selectedSubAccountRelation
      if (relation) url += `&relId=${relation.id}`
      url += `&isWhiteGloveUser=true&isOrderedReport=true&orderedReportType=${row.orderedReportType || 2}`
      window.open('/#' + url, '_blank')
    },
    /** Where the order number leads: the content plan, or the content-only order's own page. */
    planPath (row) {
      return {
        name: row.contentOnly ? 'ContentOnlyDetailWhiteGlove' : 'ContentPlanDetailWhiteGlove',
        params: { siteId: String(this.siteId), siloId: String(row.siloId) },
      }
    },
    /**
     * Source column: "Content plan: <name>", or "Content only: <name>" for an order bought outside
     * the package. An order placed before the account moved to POP Authority carries no label of
     * its own: it only stays read-only until the team works on it (row.legacy).
     */
    sourceLabel (row) {
      if (row.contentOnly) return row.planName ? `Content only: ${row.planName}` : 'Content only'
      return `Content plan: ${row.planName}`
    },
    rowKey (row) {
      return `${row.orderId}-${row.siloKeywordId}`
    },
    dateLabel (value) {
      const parsed = popAuthorityParseUtc(value)
      if (!parsed) return '—'
      return parsed.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
      })
    },
    statusSubLines (row) {
      const parts = []
      if (row.approvedAt) {
        const label = row.approvalSource === 'auto' ? 'Auto-approved' : 'Approved'
        parts.push(`${label} ${this.dateLabel(row.approvedAt)}`)
      }
      if (row.publishedAt) parts.push(`Published ${this.dateLabel(row.publishedAt)}`)
      return parts
    },
    shortUrl (url) {
      return String(url || '').replace(/^https?:\/\//, '')
    },
  },
}
</script>

<style lang="scss">
/* The global `* { font-size: .85rem; line-height: 18px }` rule (assets/main.scss) would override the
   .9rem / 1.45 base of the design (pa.css .pa-app) on every descendant, which made the bold order
   numbers and links smaller than the rest of their row; inherit instead (icon fonts keep their own
   size). Unscoped on purpose: the rule must reach the children's DOM. */
.pa-deliverables :where(*:not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.mdi):not(.fa)) {
  font-size: inherit;
  line-height: inherit;
}

/* design: the download trigger is a round orange icon like the other row actions (.pa-act); the shared
   SiloActionMenu renders a Vuetify text button, restyled here. Unscoped: the button is the child's DOM. */
.pa-deliverables__download .v-btn.v-btn--text.v-size--small {
  min-width: 30px;
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 50%;

  &::before {
    display: none;
  }

  &:hover,
  &[aria-expanded="true"] {
    background: var(--v-lightYellow2Color-base, #fff4ee);
  }

  .material-icons-outlined,
  .v-icon {
    font-size: 20px;
  }
}
</style>

<style lang="scss" scoped>
@import './setup/setup.scss';
@import './setup/designModal.scss';

.pa-deliverables {
  padding: 4px 0 40px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  /* design: the order number is bold teal text like the rest of the row, not an orange link;
     it still opens the content plan */
  .pa-table a.pa-deliverables__order {
    color: $pa-teal;
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__loading {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  &__empty-filter {
    padding: 24px 16px;
    text-align: center;
    color: $pa-muted;
    font-size: 0.95rem;
    border: 1px solid $pa-border-soft;
    border-radius: 10px;
    background: #fff;
  }

  &__price {
    font-weight: 700;
  }

  &__sub {
    font-size: 0.73rem;
    color: $pa-faint;
    margin-top: 3px;
    white-space: nowrap;
  }

  /* design ContentTab: the live URL under the keyword is a blue link (pa.css a { color: --pop-link }) */
  &__live {
    margin-top: 3px;
    font-size: 0.78rem;
    word-break: break-all;

    a {
      color: $pa-link;
      font-weight: 600;
      text-decoration: none;

      &:hover {
        color: #0061d6;
        text-decoration: underline;
      }
    }
  }

  &__acts {
    /* packed to the cell's right edge: rows carry different icons, so the "Add live URL" buttons
       line up across rows (tester, 2026-10-13) */
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    white-space: nowrap;

    /* the last icon sits next to the table edge: its tooltip grows to the left (setup.scss [data-tip]) */
    > [data-tip]:last-child::after {
      left: auto;
      right: 0;
      transform: none;
    }
  }

  &__download {
    display: inline-flex;
    align-items: center;
  }

  &__small {
    height: 28px;
    padding: 0 10px;
    font-size: 0.78rem;
  }

  &__pending {
    color: $pa-faint;
    font-weight: 600;
  }

  &__bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 14px;
  }

  &__bar-text {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: $pa-accent-soft;
    border-radius: 10px;
    padding: 9px 14px;
    font-size: 0.9rem;
    color: $pa-teal;

    .material-icons {
      font-size: 18px;
      color: $pa-accent;
    }
  }

  &__note {
    color: $pa-muted;
    font-size: 0.85rem;
    margin-top: 12px;
    max-width: 78ch;
  }

  &__admin {
    border: 1px dashed #c9a3d6;
    background: #fbf3fd;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 18px;
    max-width: 78ch;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 0.85rem;

    &-head {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &-label {
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-size: 0.7rem;
      font-weight: 800;
      color: #7a3e8f;
      background: #fff;
      border-radius: 999px;
      padding: 2px 8px;
    }

    &-row {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding-top: 8px;
      border-top: 1px solid #e7c7ee;
    }

    &-check {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    .pa-btn {
      align-self: flex-start;
    }
  }
}
</style>
